import { AuthTokens, User } from "@types";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  // Store the email of the logged in user for validation
  loggedInEmail: string | null;

  // Actions
  setUser: (user: User) => void;
  setTokens: (access: string, refresh: string) => void;
  login: (user: User, tokens: AuthTokens) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      loggedInEmail: null,

      setUser: (user) => set({ user }),

      setTokens: (access, refresh) =>
        set({
          accessToken: access,
          refreshToken: refresh,
          isAuthenticated: true,
        }),

      login: (user, tokens) =>
        set({
          user,
          accessToken: tokens.access,
          refreshToken: tokens.refresh,
          isAuthenticated: true,
          // Store the email of the user who logged in
          loggedInEmail: user.email,
        }),

      logout: () => {
        // Also clear localStorage completely to remove any stale data
        localStorage.removeItem("beyond-class-auth");
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          loggedInEmail: null,
        });
      },

      setLoading: (loading) => set({ isLoading: loading }),
    }),
    {
      name: "beyond-class-auth",
      // Version 4: Force clear all old data - fixes session switching bug
      version: 4,
      // Persist tokens AND loggedInEmail for security validation
      // User data should always come fresh from the backend API
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        loggedInEmail: state.loggedInEmail,
      }),
      // Migration to handle old data - ALWAYS clear on version mismatch
      migrate: (persistedState: unknown, version: number) => {
        console.log(`[AuthStore] Migrating from version ${version} to 4`);
        if (version < 4) {
          // Clear ALL old data and require fresh login
          console.log(
            "[AuthStore] Clearing old auth data - please login again",
          );
          localStorage.removeItem("beyond-class-auth");
          return {
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            user: null,
            isLoading: false,
            loggedInEmail: null,
          };
        }
        return persistedState as AuthState;
      },
    },
  ),
);
